import React from 'react';
import { App, Button, Drawer, Input, Space, Table, Tag, DatePicker } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { fetchReceipts, verifyReceiptsRange, type ReceiptListItem } from './receipts.api';

export function ReceiptsTable({ baseUrl, bearer }: { baseUrl: string; bearer?: string }) {
  const { message } = App.useApp();
  const [siteId, setSiteId] = React.useState<string>('');
  const [from, setFrom] = React.useState<string>(dayjs().subtract(7, 'day').toISOString());
  const [to, setTo] = React.useState<string>(dayjs().toISOString());
  const [cursor, setCursor] = React.useState<string | null>(null);
  const [nextCursor, setNextCursor] = React.useState<string | null>(null);
  const [history, setHistory] = React.useState<string[]>([]);
  const [rows, setRows] = React.useState<ReceiptListItem[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [open, setOpen] = React.useState<boolean>(false);
  const [selected, setSelected] = React.useState<ReceiptListItem | null>(null);
  const [verifyLoading, setVerifyLoading] = React.useState<boolean>(false);
  const [verifyOk, setVerifyOk] = React.useState<boolean | null>(null);

  const load = async (cur: string | null) => {
    setLoading(true);
    try {
      const res = await fetchReceipts({
        baseUrl,
        siteId: siteId || undefined,
        from,
        to,
        cursor: cur,
        limit: 50,
        bearer,
      });
      setRows(res.items);
      setNextCursor(res.nextCursor);
      setCursor(cur);
    } catch (e) {
      setRows([]);
      setNextCursor(null);
      message.error(String((e as Error).message));
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    void load(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, from, to, baseUrl]);

  const columns: ColumnsType<ReceiptListItem> = [
    {
      title: 'Timestamp',
      dataIndex: 'ts',
      key: 'ts',
      render: (v: string) => new Date(v).toLocaleString(),
    },
    { title: 'Site', dataIndex: 'siteId', key: 'siteId' },
    {
      title: 'Key',
      dataIndex: 'keyId',
      key: 'keyId',
      width: 100,
      render: (v: string) => <Tag>{v}</Tag>,
    },
    {
      title: 'Hash',
      dataIndex: 'hash',
      key: 'hash',
      render: (v: string) => <code>{v.slice(0, 12)}…</code>,
    },
    {
      title: 'Prev',
      dataIndex: 'prevHash',
      key: 'prevHash',
      render: (v: string | null) => (v ? <code>{v.slice(0, 8)}…</code> : '—'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      render: (_, r) => (
        <Space>
          <Button
            size="small"
            onClick={() => {
              setSelected(r);
              setVerifyOk(null);
              setOpen(true);
            }}
          >
            View
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 8 }} wrap>
        <Input
          placeholder="siteId"
          value={siteId}
          onChange={(e) => setSiteId(e.target.value)}
          allowClear
        />
        <DatePicker value={dayjs(from)} onChange={(d) => setFrom((d ?? dayjs()).toISOString())} />
        <DatePicker value={dayjs(to)} onChange={(d) => setTo((d ?? dayjs()).toISOString())} />
        <Button onClick={() => load(null)} loading={loading}>
          Apply
        </Button>
        <Button
          disabled={history.length === 0}
          onClick={() => {
            setHistory((h) => {
              const copy = [...h];
              const prev = copy.pop() ?? null;
              void load(prev);
              return copy;
            });
          }}
        >
          Prev
        </Button>
        <Button
          disabled={!cursor}
          onClick={() => {
            setHistory([]);
            void load(null);
          }}
        >
          First
        </Button>
        <Button disabled={!cursor} onClick={() => void load(cursor)}>
          Refresh
        </Button>
        <Button
          disabled={!nextCursor}
          onClick={() => {
            if (!nextCursor) return;
            setHistory((h) => [...h, cursor ?? '']);
            void load(nextCursor);
          }}
        >
          Next
        </Button>
      </Space>
      <Table<ReceiptListItem>
        rowKey={(r) => r.id}
        dataSource={rows}
        columns={columns}
        size="small"
        loading={loading}
        pagination={false}
      />

      <Drawer open={open} onClose={() => setOpen(false)} size={680} title="Receipt">
        {selected && (
          <div>
            <div style={{ marginBottom: 8 }}>
              <Button
                onClick={async () => {
                  setVerifyLoading(true);
                  try {
                    const res = await verifyReceiptsRange({
                      baseUrl,
                      siteId: selected.siteId,
                      subjectKey: selected.subjectKey,
                      from,
                      to,
                      bearer,
                    });
                    setVerifyOk(res.ok);
                    if (!res.ok) message.error(`Verify failed: ${res.errors?.join(', ')}`);
                  } catch (e) {
                    message.error(String((e as Error).message));
                  } finally {
                    setVerifyLoading(false);
                  }
                }}
                loading={verifyLoading}
              >
                Verify chain
              </Button>
              <Button
                style={{ marginLeft: 8 }}
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(JSON.stringify(selected, null, 2));
                    message.success('Copied to clipboard');
                  } catch {
                    message.error('Copy failed');
                  }
                }}
              >
                Copy JSON
              </Button>
              {verifyOk === true && (
                <Tag color="green" style={{ marginLeft: 8 }}>
                  valid
                </Tag>
              )}
              {verifyOk === false && (
                <Tag color="red" style={{ marginLeft: 8 }}>
                  invalid
                </Tag>
              )}
            </div>
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {JSON.stringify(
                {
                  id: selected.id,
                  ts: selected.ts,
                  siteId: selected.siteId,
                  subjectKey: selected.subjectKey,
                  keyId: selected.keyId,
                  hash: selected.hash,
                  prevHash: selected.prevHash,
                  tcfString: selected.tcfString ?? null,
                  gppString: selected.gppString ?? null,
                  gvlVersion: selected.gvlVersion ?? null,
                  payload: selected.payload ?? null,
                },
                null,
                2,
              )}
            </pre>
          </div>
        )}
      </Drawer>
    </div>
  );
}
